/**
 * defaultTelemetrySink — starter implementation for TelemetryProvider.onTrack.
 *
 * Behaviour
 * ---------
 * - Dev  (`import.meta.env.DEV`): logs events to console.debug for live
 *   inspection and dashboard testing.
 * - Prod: no-op (placeholder; consumers wire a real backend as needed).
 * - Both: forwards to window.__ANALYTICS__ when present — the existing
 *   convention used by captureError.ts and webVitals.ts.
 * - Error forwarding: when payload includes an `error` field that is an
 *   instance of Error, forwards to captureError so the error surfaces in
 *   every wired observability sink (Sentry, analytics, console).
 * - Wrapped in try/catch so it NEVER propagates a failure to the caller.
 *   Telemetry must not break product paths under any circumstance.
 *
 * Swapping backends
 * -----------------
 * Replace the `onTrack` binding in main.tsx without touching this file:
 *
 * ```tsx
 * <TelemetryProvider onTrack={(name, payload) => myBackend.track(name, payload)}>
 * ```
 *
 * exactOptionalPropertyTypes compliant.
 */

import { captureError } from '../errors/captureError';
import type { TrackFn } from './useTelemetry';

/**
 * Shape of the window.__ANALYTICS__ sink used by captureError.ts and
 * webVitals.ts.  Treat as optional — not all environments wire it.
 */
type AnalyticsSink = {
    track?: (eventName: string, payload?: Record<string, unknown>) => void;
};

/**
 * Starter telemetry sink suitable for mounting at the app root.
 *
 * In development it produces a console.debug line per event so engineers
 * can see events firing in the DevTools console while building.  In
 * production it is a silent no-op unless window.__ANALYTICS__ is wired.
 *
 * When `payload.error` is an instance of Error the error is forwarded to
 * captureError so it surfaces in Sentry, analytics, and console without
 * callers needing to duplicate that call.  The `error` field is stripped
 * from the extra context to avoid circular-reference serialisation issues.
 */
export const defaultTelemetrySink: TrackFn = (
    eventName: string,
    payload?: Record<string, unknown>,
): void => {
    // ── Dev logging ────────────────────────────────────────────────────────
    // Wrapped in its own try/catch so a console failure cannot block the
    // error-forwarding path below.
    /* eslint-disable chiefy/no-silent-catch -- Why: telemetry sinks must not propagate failure; see captureError.ts:78 */
    try {
        // Dev-only console output — esbuild drops console.* in prod bundles
        // (see vite.config.ts esbuild.drop), so this never ships to users.
        if (import.meta.env.DEV) {
            console.debug('[telemetry]', eventName, payload ?? {});
        }
    } catch {
        // Swallow — see captureError.ts:78 for the canonical rationale.
    }

    // ── Error forwarding ───────────────────────────────────────────────────
    // When a caller embeds an Error instance in the payload, surface it
    // through captureError so Sentry and analytics pick it up.  Strip the
    // `error` key from the extra context to prevent circular-reference
    // serialisation issues inside downstream sinks.
    try {
        if (payload !== undefined && payload['error'] instanceof Error) {
            const { error, ...rest } = payload;
            captureError(error as Error, {
                scope: 'telemetry',
                op: eventName,
                ...(Object.keys(rest).length > 0 ? { extra: rest } : {}),
            });
        }
    } catch {
        // Swallow — see captureError.ts:78 for the canonical rationale.
    }

    // ── Analytics forwarding ───────────────────────────────────────────────
    try {
        // Forward to window.__ANALYTICS__ when present.  This wires into the
        // same sink that captureError.ts (lines 99-106) and webVitals.ts
        // (lines 71-95) already use.  Safe when not wired — just a no-op.
        const analytics = (window as unknown as Record<string, unknown>)['__ANALYTICS__'] as AnalyticsSink | undefined;
        if (analytics != null && typeof analytics.track === 'function') {
            analytics.track(eventName, payload);
        }
    } catch {
        // Swallow — see captureError.ts:78 for the canonical rationale.
    }
    /* eslint-enable chiefy/no-silent-catch */
};
